import { Component } from '@theme/component';

const MOBILE_MAX_WIDTH = 740;

/**
 * Video component resize updates
 * @extends {Component}
 */
class VideoModule extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.#fixHostedVideoSources();
    this.#initEventListeners();
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.#initResize);
    if (this.#initIframeVideo) {
      window.removeEventListener('load', this.#iframeLoadListener);
    }
  }

  /**
   * On non-Safari browsers, remove HLS sources from muted autoplay videos.
   * Chrome 107+ partially accepts application/x-mpegURL but stalls after the
   * first segment — stripping those sources forces Chrome onto the MP4 track,
   * then an explicit play() call handles the autoplay timing race.
   */
  #fixHostedVideoSources() {
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    if (isSafari) return;

    this.querySelectorAll('video[autoplay][muted]').forEach((video) => {
      const hlsSources = video.querySelectorAll('source[type="application/x-mpegURL"]');
      if (hlsSources.length === 0) return;

      hlsSources.forEach((source) => source.remove());
      video.load();

      video.addEventListener('canplay', () => {
        video.play().catch(() => {});
      }, { once: true });
    });
  }

  #initEventListeners() {
    const iframeVideo = this.querySelector('[data-external-video]');
    window.addEventListener('resize', this.#initResize);

    if (iframeVideo) {
      window.addEventListener('load', this.#iframeLoadListener(iframeVideo));
    }
  }

  #iframeLoadListener = (iframeVideo) => {
    this.#initIframeVideo(iframeVideo);
  }

  #initResize = () => {
    let resizedTimeout = 0;
    clearTimeout(resizedTimeout);
    resizedTimeout = setTimeout(() => this.#updateVideoSource(), 200);
  }

  #initIframeVideo = (iframeVideo) => {
    loadIframeVideo(iframeVideo);
  }

  #updateVideoSource() {
    const hostedVideos = this.querySelectorAll('video');
    const externalVideoContainer = this.querySelectorAll('[data-external-video]');
    if (hostedVideos) {
      updateHostedVideo(hostedVideos);
    }
    if (externalVideoContainer) {
      updateExternalVideo(externalVideoContainer);
    }
  };
}

/**
 * Get user's browser rendering video file type
 */
function getVideoFileType() {
  const video = document.createElement('video');
  return video.canPlayType('application/vnd.apple.mpegurl') !== '' ? 'application/x-mpegURL' : 'video/mp4';
}

/**
 * Updates Shopify hosted video based on a user's breakpoint
 * @param {NodeListOf<HTMLVideoElement>} videoSources - Video source options
 */
function updateHostedVideo(videoSources) {
  videoSources.forEach((video) => {
    const mediaQuery = window.innerWidth < 740 ? '(max-width: 739px)' : '(min-width: 740px)';
    const fileType = getVideoFileType();
    const resizedVideoSrc = video.querySelector(
      `source[type="${fileType}"][media="${mediaQuery}"]`
    );

    if (!resizedVideoSrc) return;

    const newSrc = resizedVideoSrc.src;
    if (!video.currentSrc.includes(newSrc)) {
      video.appendChild(resizedVideoSrc);
      video.load();
      video.play();
    }
  });
}

/**
 * Updates external iframe hosted video based on a user's breakpoint
 * @param {NodeListOf<Element>} externalVideoContainer - External video container
 */
function updateExternalVideo(externalVideoContainer) {
  if (!externalVideoContainer) return;

  externalVideoContainer.forEach((container) => {
    const isMobile = window.innerWidth < MOBILE_MAX_WIDTH;
    const previousMedia = container.getAttribute('data-current-media');
    const currentMedia = isMobile ? 'mobile' : 'desktop';
    
    if (currentMedia != previousMedia) {
      // Check if iframe already exists
      let hasCurrentIframe = false;
      const videoIframes = container.querySelectorAll(`iframe[data-media-type]`);
      videoIframes?.forEach((iframe) => {
        if (iframe.getAttribute('data-media-type') == currentMedia) {
          iframe.style.display = 'block';
          hasCurrentIframe = true;
        } else {
          iframe.style.display = 'none';
        }
      });

      // load new iframe video
      if (!hasCurrentIframe) {
        loadIframeVideo(container);
      }
    }
  });
}

/**
 * Loads iframe video
 * @param {Element} container - External video container
 */
function loadIframeVideo(container) {
  if (!container) return;

  const mobileSrc = container.getAttribute('data-mobile-src');
  const desktopSrc = container.getAttribute('data-desktop-src');

  if (mobileSrc && desktopSrc) {
    const isMobile = window.innerWidth < MOBILE_MAX_WIDTH;
    const currentMedia = isMobile ? 'mobile' : 'desktop';
    const iframe = document.createElement('iframe');
    iframe.setAttribute('allow', 'autoplay; encrypted-media');
    iframe.setAttribute('allowfullscreen', '');
    iframe.setAttribute('loading', 'lazy');
    iframe.setAttribute('data-media-type', currentMedia);
    iframe.src = isMobile ? mobileSrc : desktopSrc;
    container.appendChild(iframe);
  }
}

if (!customElements.get('video-module')) {
  customElements.define('video-module', VideoModule);
}